import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  Modal, 
  IconButton,
  Fade,
  Backdrop
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SEO from '../components/SEO';

// Image imports from the newly added gallery folder
import img1 from '../images/gallery/WhatsApp Image 2026-04-13 at 1.29.38 PM.jpeg';
import img2 from '../images/gallery/WhatsApp Image 2026-04-13 at 1.30.25 PM.jpeg';
import img3 from '../images/gallery/WhatsApp Image 2026-04-13 at 1.30.26 PM (1).jpeg';
import img4 from '../images/gallery/WhatsApp Image 2026-04-13 at 1.30.26 PM.jpeg';
import img5 from '../images/gallery/WhatsApp Image 2026-04-13 at 1.30.27 PM.jpeg';
import img6 from '../images/gallery/WhatsApp Image 2026-04-13 at 1.35.38 PM (1).jpeg';
import img7 from '../images/gallery/WhatsApp Image 2026-04-13 at 1.35.38 PM.jpeg';
import img8 from '../images/gallery/WhatsApp Image 2026-04-13 at 1.36.32 PM.jpeg';

const galleryItems = [
  {
    title: 'Automatic Vending Solution',
    description: 'Our flagship white automatic vending machine, providing easy access to hygiene products.',
    image: img8,
  },
  {
    title: 'Vending Machine Installation',
    description: 'A blue wall-mounted vending system designed for durability and ease of use.',
    image: img7,
  },
  {
    title: 'Safe Disposal Demonstration',
    description: 'A dedicated team member demonstrating the eco-friendly disposal process.',
    image: img1,
  },
  {
    title: 'Awareness & Outreach',
    description: 'Engaging with students and the community to promote menstrual health and hygiene.',
    image: img2,
  },
  {
    title: 'Inauguration Ceremony',
    description: 'A proud moment during the inauguration of our hygiene solutions at a local institution.',
    image: img4,
  },
  {
    title: 'Operating Instructions',
    description: 'Providing clear, step-by-step guidance on how to operate our vending and disposal units.',
    image: img3,
  },
  {
    title: 'Technology Training',
    description: 'Training local staff and users on the safe and effective use of Fem Tech equipment.',
    image: img5,
  },
  {
    title: 'Horizontal Vending Unit',
    description: 'Compact horizontal vending unit suitable for various installation requirements.',
    image: img6,
  },
];

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '95vw',
  maxHeight: '95vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 0.5,
  borderRadius: 2,
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

function Gallery() {
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleOpen = (item) => {
    setSelectedItem(item);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  return (
    <Box sx={{ 
      backgroundColor: '#fdfbfd', 
      pt: { xs: 14, md: 18 },
      pb: { xs: 6, md: 10 },
      backgroundImage: 'radial-gradient(at 0% 0%, hsla(253,16%,7%,0.03) 0, transparent 50%), radial-gradient(at 50% 0%, hsla(225,39%,30%,0.03) 0, transparent 50%)'
    }}>
      <SEO 
        title="Gallery"
        description="View our recent installations, awareness programs, and exactly how Fem Tech Solution builds reliable hygiene management projects."
        keywords="gallery, fem tech installations, hygiene programs, images, sanitary dispensers"
      />
      <Container maxWidth="lg">
        {/* Gallery Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(45deg, #7e57c2 30%, #f06292 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              textTransform: 'uppercase',
              letterSpacing: '2px',
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Our Gallery
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#666',
              maxWidth: '700px',
              mx: 'auto',
              fontWeight: 400,
              lineHeight: 1.6,
              px: 2
            }}
          >
            Take a look at our installations, awareness programs, and the advanced technology we bring to hygiene management.
          </Typography>
          <Box sx={{ 
            width: '80px', 
            height: '4px', 
            background: 'linear-gradient(90deg, #7e57c2, #f06292)', 
            mx: 'auto', 
            mt: 3, 
            borderRadius: '2px' 
          }} />
        </Box>

        <Grid container spacing={4}>
          {galleryItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                onClick={() => handleOpen(item)}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: '1px solid rgba(126, 87, 194, 0.1)',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: '0 20px 40px rgba(126, 87, 194, 0.15)',
                    '& .MuiCardMedia-root': {
                      transform: 'scale(1.1)',
                    }
                  }
                }}
              >
                <Box sx={{ overflow: 'hidden', position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="300"
                    image={item.image}
                    alt={item.title}
                    sx={{ 
                      objectFit: 'cover',
                      transition: 'transform 0.6s ease',
                    }}
                  />
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    width: '100%', 
                    height: '100%', 
                    background: 'linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.4) 100%)',
                    opacity: 0.8
                  }} />
                </Box>
                <CardContent sx={{ flexGrow: 1, backgroundColor: '#fff', p: 3 }}>
                  <Typography gutterBottom variant="h6" component="div" sx={{ 
                    color: '#4d2c91', 
                    fontWeight: 700,
                    fontSize: '1.1rem'
                  }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.5 }}>
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Lightbox Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.92)' }
        }}
      >
        <Fade in={open}>
          <Box sx={modalStyle}>
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                zIndex: 1,
                '&:hover': { bgcolor: '#f06292' }
              }}
            >
              <CloseIcon />
            </IconButton>
            {selectedItem && (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ position: 'relative', flexGrow: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#000' }}>
                  <Box 
                    component="img"
                    src={selectedItem.image}
                    alt={selectedItem.title}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: 'white' }}>
                  <Typography variant="h5" sx={{ color: '#4d2c91', fontWeight: 700, mb: 1 }}>
                    {selectedItem.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    {selectedItem.description}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}

export default Gallery;
